import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { api, formatApiError } from "@/lib/api";
import { logger } from "@/lib/logger";
import { ArrowRight, Download, Moon, Heart, Brain, Activity, RotateCcw } from "lucide-react";

function Mark({ color = "#4FBEBE", size = 22 }) {
  return (
    <svg viewBox="0 0 672 727" width={size} height={(size * 727) / 672} aria-hidden="true">
      <g fill={color}>
        <rect x="36" y="36" width="600" height="135" rx="29.7" />
        <rect x="126" y="296" width="420" height="135" rx="29.7" />
        <rect x="36" y="556" width="600" height="135" rx="29.7" />
      </g>
    </svg>
  );
}

// tier -> emphasis colour (kept high-contrast on dark navy)
const TIER_TONE = {
  low:      { chip: "#9FE9D6", label: "LOWER-RISK PROFILE" },
  moderate: { chip: "#FFCC66", label: "MODERATE RISK"       },
  high:     { chip: "#FF9F93", label: "HIGH RISK"           },
};

export default function Results() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [downloading, setDownloading] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => {
    api.get(`/quiz/submissions/${id}`).then((r) => setData(r.data))
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || e.message));
  }, [id]);

  const downloadPdf = async () => {
    if (!reportRef.current) return;
    setDownloading(true);
    // Reveal print-only branded header/footer for the duration of the capture.
    reportRef.current.classList.add("rs-pdf-mode");
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      const filename = `Somveil_Report_${(data?.name || "Guest").replace(/\s+/g, "_")}_${id.slice(0, 8)}.pdf`;
      const opt = {
        margin:      [14, 12, 16, 12],
        filename,
        image:       { type: "jpeg", quality: 0.96 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: "#FAF7F2" },
        jsPDF:       { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak:   { mode: ["avoid-all", "css", "legacy"] },
      };
      await html2pdf().set(opt).from(reportRef.current).save();
    } catch (e) {
      logger.error("PDF download failed:", e);
      alert("PDF download failed — please try again or use your browser's Print → Save as PDF option.");
    } finally {
      if (reportRef.current) reportRef.current.classList.remove("rs-pdf-mode");
      setDownloading(false);
    }
  };

  if (err) {
    return (
      <div className="rs-page">
        <Nav theme="dark" />
        <div className="rs-hero"><div className="rs-inner"><p className="rs-err">{err}</p></div></div>
        <Footer />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="rs-page">
        <Nav theme="dark" />
        <div className="rs-hero"><div className="rs-inner"><div className="spinner-teal" /></div></div>
        <Footer />
      </div>
    );
  }

  const r = data.result;
  const tier = r.riskTier;
  const tone = TIER_TONE[tier] || TIER_TONE.low;
  const highFlags = r.flaggedSymptoms.filter((f) => f.type === "high");
  const secondaryFlags = r.flaggedSymptoms.filter((f) => f.type === "secondary");

  const WHY_ITEMS = [
    { icon: <Heart size={20} />,    title: "Your heart & blood pressure", desc: "Untreated OSA doubles cardiovascular mortality over 10 years and triples stroke risk. Many 'resistant' hypertension cases resolve with sleep apnea treatment." },
    { icon: <Brain size={20} />,    title: "Your focus & memory",         desc: "Chronic oxygen drops during sleep reduce cognitive performance by up to 30% — memory, decision-making, and concentration all suffer silently." },
    { icon: <Activity size={20} />, title: "Your metabolism",              desc: "OSA and Type 2 diabetes share a bidirectional relationship. Treating one often dramatically improves control of the other." },
    { icon: <Moon size={20} />,     title: "Real, restorative sleep",      desc: "Most people on CPAP therapy describe their first week as 'the first real sleep in years.' The transformation is often immediate." },
  ];

  return (
    <div className="rs-page" data-testid="results-page">
      <Nav theme="dark" />

      {/* HERO BANNER */}
      <section className="rs-hero">
        <div aria-hidden="true" className="rs-hero-orb rs-hero-orb-1" />
        <div aria-hidden="true" className="rs-hero-orb rs-hero-orb-2" />
        <div className="rs-inner">
          <div className="rs-hero-eyebrow">
            <Mark color="#4FBEBE" size={20} />
            <span>Your result</span>
            <span className="rs-hero-chip" style={{ color: tone.chip, borderColor: `${tone.chip}44` }} data-testid="risk-badge">
              {tone.label}
            </span>
          </div>
          <h1 className="rs-hero-h" data-testid="results-headline">
            {tier === "high" &&
              <>Your responses strongly suggest <em>sleep apnea.</em></>}
            {tier === "moderate" &&
              <>Your responses suggest <em>moderate risk.</em></>}
            {tier === "low" &&
              <>Your responses suggest <em>a lower-risk profile.</em></>}
          </h1>
          <p className="rs-hero-sub">
            Based on 20 validated screening questions (STOP-BANG + Epworth). This is a screening, not a diagnosis.
          </p>
        </div>
      </section>

      {/* REPORT BODY */}
      <section className="rs-body">
        <div className="rs-inner rs-inner-narrow" ref={reportRef}>
          {/* PDF-only branded header — visible only when parent has .rs-pdf-mode */}
          <div className="rs-print-only rs-pdf-header">
            <div className="rs-pdf-brand">
              <img src="/somveil-wordmark-primary.png" alt="Somveil" className="rs-pdf-logo" />
              <div className="rs-pdf-meta">
                <div className="rs-pdf-eyebrow">Personal OSA Risk Report</div>
                <div className="rs-pdf-id">Report ID {id.slice(0, 8).toUpperCase()} · {new Date(data.created_at).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" })}</div>
              </div>
            </div>
            <div className="rs-pdf-hr" />
            <div className="rs-pdf-recipient">
              {data.name ? <><span className="rs-pdf-label">Prepared for</span> <strong>{data.name}</strong></> : <><span className="rs-pdf-label">Prepared for</span> <em>Guest</em></>}
              {data.city ? <span className="rs-pdf-city">·  {data.city}</span> : null}
            </div>
            <div className="rs-pdf-verdict">
              <span className="rs-pdf-verdict-chip" style={{ borderColor: tone.chip, color: tone.chip }}>{tone.label}</span>
              <span className="rs-pdf-verdict-txt">
                {tier === "high" && "Your responses strongly suggest sleep apnea."}
                {tier === "moderate" && "Your responses suggest moderate risk."}
                {tier === "low" && "Your responses suggest a lower-risk profile."}
              </span>
            </div>
          </div>

          {/* SCORE CARDS */}
          <div className="rs-score-grid" data-testid="score-cards">
            <div className="rs-score-card">
              <div className="rs-score-n" data-testid="score-percentage">
                {r.percentageScore}<span className="rs-score-pct">%</span>
              </div>
              <div className="rs-score-l">Overall score</div>
            </div>
            <div className="rs-score-card">
              <div className="rs-score-n rs-coral" data-testid="high-flag-count">{r.highFlagCount}</div>
              <div className="rs-score-l">High-priority flags</div>
            </div>
            <div className="rs-score-card">
              <div className="rs-score-n rs-amber" data-testid="secondary-flag-count">{r.secondaryFlagCount}</div>
              <div className="rs-score-l">Secondary flags</div>
            </div>
          </div>

          {/* SUMMARY */}
          <div className="rs-summary-card">
            <div className="hn-eyebrow-dark" style={{ marginBottom: 12 }}>In plain language</div>
            <p className="rs-summary" data-testid="results-summary">{r.summary}</p>
          </div>

          {/* FLAGGED SYMPTOMS */}
          {r.flaggedSymptoms.length > 0 && (
            <div className="rs-flags-wrap">
              <div className="hn-eyebrow-dark">What you flagged</div>
              <p className="rs-flags-lede">
                These are the specific symptoms and risk factors you indicated — the ones a sleep specialist would want to look at.
              </p>
              <div className="rs-flag-list" data-testid="flag-list">
                {highFlags.map((f) => (
                  <div key={`hf-${f.id}`} className="rs-flag-item rs-flag-high" data-testid={`flag-high-${f.id}`}>
                    <span className="rs-flag-dot" />
                    <span className="rs-flag-txt"><strong>High priority:</strong> {f.text}</span>
                  </div>
                ))}
                {secondaryFlags.map((f) => (
                  <div key={`sf-${f.id}`} className="rs-flag-item rs-flag-secondary" data-testid={`flag-secondary-${f.id}`}>
                    <span className="rs-flag-dot" />
                    <span className="rs-flag-txt">{f.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* PDF-only branded footer */}
          <div className="rs-print-only rs-pdf-footer">
            <div className="rs-pdf-hr" />
            <div className="rs-pdf-foot-row">
              <img src="/somveil-wordmark-primary.png" alt="Somveil" className="rs-pdf-foot-logo" />
              <div className="rs-pdf-foot-txt">
                <div><strong>Somveil</strong> · India&apos;s Sleep Apnea Discovery Platform</div>
                <div className="rs-pdf-foot-sub">somveil.in  ·  hello@somveil.in  ·  A HMH Labz LLP Unit</div>
              </div>
            </div>
            <div className="rs-pdf-disclaimer">
              This report is a screening tool based on validated questionnaires (STOP-BANG, Epworth). It is <strong>not a medical diagnosis</strong>. Only a qualified sleep specialist, using a sleep study, can diagnose obstructive sleep apnea. Somveil is a health information and referral platform — not a medical device, telemedicine service, or diagnostic provider.
            </div>
          </div>
        </div>

        {/* PRIMARY CTA — dark card */}
        <div className="rs-inner rs-inner-narrow">
          <div className="rs-cta-card">
            <div aria-hidden="true" className="rs-cta-orb" />
            <div className="rs-cta-inner">
              <div className="hn-eyebrow-teal" style={{ marginBottom: 14 }}>What to do next</div>
              <h2 className="rs-cta-h">
                {tier === "high" || tier === "moderate"
                  ? <>Don&apos;t leave this unanswered. <em>Get tested.</em></>
                  : <>Keep this for your records. <em>Stay aware.</em></>}
              </h2>

              <div className="rs-cta-row">
                <button
                  onClick={downloadPdf}
                  disabled={downloading}
                  className="rs-btn-cream"
                  data-testid="results-download-pdf"
                >
                  <Download size={16} /> {downloading ? "Preparing PDF…" : "Download the test results"}
                </button>
                <Link to={`/book?submissionId=${id}`} className="rs-btn-teal" data-testid="results-book-cta">
                  Book your sleep study today <ArrowRight size={16} />
                </Link>
              </div>

              <p className="rs-cta-note" data-testid="why-book-paragraph">
                <strong>Why booking a sleep study is essential:</strong> this quiz is a screening tool — it tells you whether your pattern of symptoms is consistent with OSA. It <em>cannot confirm diagnosis.</em> A sleep study — a single overnight test either at home or in a lab — is the only way to definitively know whether you have sleep apnea, how severe it is, and what treatment (if any) will actually help. Without it, you&apos;re left guessing about something that, untreated, directly raises your risk of heart disease, stroke, diabetes, and cognitive decline. A night&apos;s test. An answer that could change the next thirty years of your health.
              </p>
            </div>
          </div>
        </div>

        {/* WHY THIS MATTERS */}
        <div className="rs-inner rs-inner-narrow rs-why-block">
          <div className="hn-eyebrow-dark">Why this matters</div>
          <h2 className="rs-why-h">
            What untreated sleep apnea is <em>actually doing</em> to you.
          </h2>
          <div className="rs-why-grid">
            {WHY_ITEMS.map((w, i) => (
              <div key={w.title} className="rs-why-card" data-testid={`why-item-${i}`}>
                <div className="rs-why-icon">{w.icon}</div>
                <h4>{w.title}</h4>
                <p>{w.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* DISCLAIMER + RETAKE */}
        <div className="rs-inner rs-inner-narrow rs-tail">
          <div className="rs-disclaimer" data-testid="results-disclaimer">
            <strong>Important:</strong> This report is a screening tool based on validated questionnaires (STOP-BANG, Epworth). It is not a medical diagnosis. Only a qualified sleep specialist, using a sleep study, can diagnose obstructive sleep apnea. Somveil is a health information and referral platform — not a medical device, telemedicine service, or diagnostic provider.
          </div>
          <div className="rs-tail-actions">
            <Link to="/quiz" className="rs-btn-ghost-dark" data-testid="results-retake-btn">
              <RotateCcw size={14} /> Retake the quiz
            </Link>
            <Link to="/doctors" className="rs-btn-ghost-dark" data-testid="results-explore-specialists">
              Browse specialists without booking
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
